import React, { useState, useEffect, useRef } from "react";
import { captureMessage } from "@sentry/react";
import { useClerk, useSignIn, useUser } from "@clerk/clerk-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import LOGO from "../assets/images/Diligent - 2025 Branding 2.svg"; // Adjust the path as necessary
import { clearAllStickyFilters } from "../utils/stickyFilterStorage";

const TICKET_ERROR =
  "Unable to activate your invite link. Please request a new invite or contact support.";
const SWITCH_SESSION_ERROR =
  "Unable to switch to the invite session. Please sign out and reopen the link.";

const clerkMessage = (err, fallback) => err.errors?.[0]?.longMessage || fallback;

const primaryButtonClasses =
  "w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm";

// If another session is already active (e.g. tester logged in as admin), sign out first -
// otherwise updatePassword would change that user's password, not the invitee's.
async function signOutForInvite({ signOut, setError, setIsConsumingTicket, signedOutRef }) {
  if (signedOutRef.current) {
    return;
  }
  signedOutRef.current = true;
  setIsConsumingTicket(true);
  setError("");
  try {
    // Stay on the invite URL so the effect can consume the ticket after sign-out.
    clearAllStickyFilters();
    await signOut({
      redirectUrl: `${window.location.pathname}${window.location.search}`,
    });
  } catch (err) {
    setError(clerkMessage(err, SWITCH_SESSION_ERROR));
    signedOutRef.current = false;
    setIsConsumingTicket(false);
  }
}

async function exchangeTicket({
  signIn,
  setActive,
  navigate,
  signInToken,
  setError,
  setTicketFailed,
}) {
  try {
    const result = await signIn.create({
      strategy: "ticket",
      ticket: signInToken,
    });

    if (result.status === "complete" && result.createdSessionId) {
      await setActive({ session: result.createdSessionId });
      // Clerk tickets are single-use. Drop ?token= so a refresh keeps the
      // invitee session instead of signing out and re-consuming a spent ticket.
      navigate("/set-password", { replace: true });
      return;
    }
    setTicketFailed(true);
    captureMessage("set-password: sign-in ticket did not complete", {
      level: "warning",
      extra: { status: result.status },
    });
  } catch (err) {
    setTicketFailed(true);
    setError(clerkMessage(err, TICKET_ERROR));
    // Never include the token itself.
    captureMessage("set-password: sign-in ticket failed", {
      level: "warning",
      extra: { code: err.errors?.[0]?.code, httpStatus: err.status },
    });
  }
}

// Consume the Clerk sign-in token so the invitee has a session before updatePassword.
// Each token is attempted at most once and the guard is never reset on failure: the effect
// depends on `signIn`, which can change identity after a failed call, and resetting the
// guard made the page re-send the same spent token in a loop until Clerk answered 429
// "Too many requests" (which then blocked the whole IP).
function useInviteTicket({
  signInToken,
  isReady,
  user,
  signIn,
  setActive,
  signOut,
  navigate,
  setError,
}) {
  const [isConsumingTicket, setIsConsumingTicket] = useState(false);
  const [ticketFailed, setTicketFailed] = useState(false);
  const attemptedTicketRef = useRef(null);
  const signedOutForInviteRef = useRef(false);

  useEffect(() => {
    if (!isReady || !signIn || !setActive || !signOut) {
      return;
    }
    if (!signInToken || attemptedTicketRef.current === signInToken) {
      return;
    }

    const run = async () => {
      if (user) {
        await signOutForInvite({
          signOut,
          setError,
          setIsConsumingTicket,
          signedOutRef: signedOutForInviteRef,
        });
        return;
      }

      attemptedTicketRef.current = signInToken;
      setIsConsumingTicket(true);
      setTicketFailed(false);
      setError("");
      await exchangeTicket({
        signIn,
        setActive,
        navigate,
        signInToken,
        setError,
        setTicketFailed,
      });
      setIsConsumingTicket(false);
    };

    run();
  }, [isReady, signIn, setActive, signOut, signInToken, user, navigate, setError]);

  return { isConsumingTicket, ticketFailed };
}

// Legacy email-only flow: send reset code (form still expects a signed-in user for updatePassword).
// Skipped when a sign-in token is present - that path establishes the session instead.
function useLegacyResetCode({
  email,
  signInToken,
  isLoaded,
  user,
  signIn,
  setError,
  setIsLoading,
}) {
  const attemptedEmailRef = useRef(null);

  useEffect(() => {
    if (!isLoaded || !signIn || !email || signInToken || user) {
      return;
    }
    if (attemptedEmailRef.current === email) {
      return;
    }
    attemptedEmailRef.current = email;

    const sendResetCode = async () => {
      setIsLoading(true);
      setError("");
      try {
        await signIn.create({
          strategy: "reset_password_email_code",
          identifier: email,
        });
      } catch (err) {
        setError(clerkMessage(err, "Failed to send reset code"));
      } finally {
        setIsLoading(false);
      }
    };

    sendResetCode();
  }, [isLoaded, email, signInToken, user, signIn, setError, setIsLoading]);
}

const pickView = ({ ticketFailed, user, signInToken, isConsumingTicket, isLoading, email }) => {
  if (ticketFailed && !user) {
    return "failed";
  }
  const ticketPending =
    Boolean(signInToken) && !ticketFailed && (isConsumingTicket || !user);
  if (ticketPending) {
    return "loading";
  }
  if (isLoading && !user && email && !signInToken) {
    return "loading";
  }
  return "form";
};

const loadingMessage = (signInToken, email) =>
  signInToken ? "Activating your invite..." : `Sending reset code to ${email}...`;

const SpinnerIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

const InviteFailedPanel = ({ onContinue }) => (
  <div className="space-y-4 py-6 text-center">
    <p className="text-sm font-medium text-gray-800">
      This link has already been used or is no longer valid.
    </p>
    <p className="text-sm text-gray-600">
      Enter your email on the sign-in page and we&apos;ll send you a new code to
      get in. Please don&apos;t refresh or retry this link.
    </p>
    <button type="button" onClick={onContinue} className={primaryButtonClasses}>
      Continue to sign in
    </button>
  </div>
);

const LoadingPanel = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-8">
    <SpinnerIcon className="animate-spin h-10 w-10 text-indigo-600 mb-4" />
    <p className="text-sm text-gray-600">{message}</p>
  </div>
);

const ErrorBanner = ({ message }) => (
  <div className="rounded-md bg-red-50 p-3">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg
          className="h-5 w-5 text-red-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm font-medium text-red-800">{message}</p>
      </div>
    </div>
  </div>
);

const PasswordField = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  minLength,
  invalid = false,
  children,
}) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    <div className="mt-1 relative rounded-md shadow-sm">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <LockClosedIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
      </div>
      <input
        type="password"
        id={id}
        className={`focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md border bg-white ${
          invalid ? "border-red-500" : ""
        }`}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        minLength={minLength}
      />
    </div>
    {children}
  </div>
);

const PasswordForm = ({
  password,
  onPasswordChange,
  confirmPassword,
  onConfirmPasswordChange,
  passwordsMatch,
  error,
  isLoading,
  isConsumingTicket,
  hasUser,
  onSubmit,
}) => {
  const submitDisabled = isLoading || isConsumingTicket || !passwordsMatch || !hasUser;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <PasswordField
        id="password"
        label="New Password"
        placeholder="Enter your new password"
        value={password}
        onChange={onPasswordChange}
        minLength={8}
      >
        <p className="mt-1 text-xs text-gray-500">
          Password must be at least 8 characters long
        </p>
      </PasswordField>

      <PasswordField
        id="confirmPassword"
        label="Confirm Password"
        placeholder="Confirm your new password"
        value={confirmPassword}
        onChange={onConfirmPasswordChange}
        invalid={!passwordsMatch}
      >
        {!passwordsMatch && (
          <p className="mt-1 text-xs text-red-600">Passwords do not match</p>
        )}
      </PasswordField>

      {error && <ErrorBanner message={error} />}

      <div className="pt-2">
        <button
          type="submit"
          disabled={submitDisabled}
          className={`${primaryButtonClasses} ${
            submitDisabled ? "opacity-75 cursor-not-allowed" : ""
          }`}
        >
          {isLoading && <SpinnerIcon className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />}
          {isLoading ? "Setting Password..." : "Set Password"}
        </button>
      </div>
    </form>
  );
};

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user, isLoaded: isUserLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { isLoaded, signIn, setActive } = useSignIn();
  const [searchParams] = useSearchParams();

  // Invite emails embed Clerk sign-in token (ticket strategy). Legacy links may still use ?email=.
  const signInToken = searchParams.get("token");
  const email = searchParams.get("email");

  // Don't show a mismatch error while the confirm field is empty
  useEffect(() => {
    setPasswordsMatch(!confirmPassword || password === confirmPassword);
  }, [password, confirmPassword]);

  const { isConsumingTicket, ticketFailed } = useInviteTicket({
    signInToken,
    isReady: Boolean(isLoaded && isUserLoaded),
    user,
    signIn,
    setActive,
    signOut,
    navigate,
    setError,
  });

  useLegacyResetCode({
    email,
    signInToken,
    isLoaded,
    user,
    signIn,
    setError,
    setIsLoading,
  });

  // Reset the user's password (requires an active Clerk session from the invite ticket).
  async function reset(e) {
    e.preventDefault();

    if (password !== confirmPassword) {
      setPasswordsMatch(false);
      return;
    }

    if (!user) {
      setError(
        "Your invite session is not active yet. Please wait a moment or reopen the link from your email."
      );
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await user.updatePassword({
        newPassword: password,
      });
      navigate("/");
    } catch (err) {
      setError(clerkMessage(err, "An error occurred during password reset"));
    } finally {
      setIsLoading(false);
    }
  }

  if (!isLoaded || !isUserLoaded) {
    return null;
  }

  const view = pickView({
    ticketFailed,
    user,
    signInToken,
    isConsumingTicket,
    isLoading,
    email,
  });

  let body;
  if (view === "failed") {
    body = <InviteFailedPanel onContinue={() => navigate("/", { replace: true })} />;
  } else if (view === "loading") {
    body = <LoadingPanel message={loadingMessage(signInToken, email)} />;
  } else {
    body = (
      <PasswordForm
        password={password}
        onPasswordChange={setPassword}
        confirmPassword={confirmPassword}
        onConfirmPasswordChange={setConfirmPassword}
        passwordsMatch={passwordsMatch}
        error={error}
        isLoading={isLoading}
        isConsumingTicket={isConsumingTicket}
        hasUser={Boolean(user)}
        onSubmit={reset}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
      {/* Set fixed width and height for square shape */}
      <div className="w-[400px] rounded-lg bg-white shadow-xl overflow-hidden flex flex-col">
        {/* Header section with fixed height */}
        <div className="p-4 flex flex-col items-center">
          <div className="flex justify-center items-center mt-2">
            <img src={LOGO} className="w-1/2 aspect-auto" alt="Logo" />
          </div>
          <h2 className="text-lg text-center font-bold mt-3 text-gray-800">
            Create Your Password
          </h2>
          <p className="text-sm text-center text-gray-500 mt-1">
            Choose a secure password to access your account
          </p>
        </div>

        {/* Form section with flex-grow to fill remaining space */}
        <div className="px-6 flex-grow overflow-y-auto">
          <div className="w-full">{body}</div>
        </div>

        {/* Footer section with fixed height */}
        <div className="bg-gray-50 px-6 py-3 flex justify-end mt-auto"></div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
